import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { Song } from '../types';

export function WaveRadioPage() {
  const [songs, setSongs] = useState<Song[]>([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [error, setError] = useState('');
  const audio = useRef<HTMLAudioElement>(null);
  const playable = useMemo(() => songs.filter((song) => !!song.fileUrl), [songs]);
  const current = playable[index] || null;

  useEffect(() => {
    api.wave().then((data) => setSongs(data.songs || [])).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!audio.current || !current) return;
    audio.current.src = api.audioUrl(current.fileUrl) || '';
    if (playing) audio.current.play().catch(() => { setPlaying(false); setError('This track could not be played.'); });
  }, [current?.id]);

  const next = () => {
    if (!playable.length) return;
    setIndex((value) => shuffle && playable.length > 1
      ? (value + 1 + Math.floor(Math.random() * (playable.length - 1))) % playable.length
      : (value + 1) % playable.length);
  };
  const previous = () => playable.length && setIndex((value) => (value - 1 + playable.length) % playable.length);
  const toggle = async () => {
    if (!audio.current || !current) return;
    setError('');
    try {
      if (playing) { audio.current.pause(); setPlaying(false); }
      else { await audio.current.play(); setPlaying(true); await api.play(current.id); }
    } catch { setError('This track could not be played. Please try another one.'); }
  };

  return <div className="wave-radio-page">
    <div className="card wave-radio-intro">
      <span className="chip chip-gold">24/7 IAMMUZIK</span>
      <h1>Wave Radio</h1>
      <p>Every approved clean track placed on The Wave stays in this station’s catalog for listeners to discover.</p>
    </div>
    <div className="wave-radio-scene" aria-label="Wave Radio sitting above a spinning world globe">
      <img className="wave-radio-globe" src="/assets/wave-globe.webp" alt="Spinning world globe" />
      <img className="wave-radio-object" src="/assets/wave-radio.webp" alt="Front-facing old-school Wave Radio" />
    </div>
    <div className="card wave-radio-controls">
      <audio ref={audio} onEnded={next} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
      {current ? <>
        <div className="now-playing"><span className="live-pulse-dot" /><div><small>NOW PLAYING</small><h2>{current.title}</h2><p>{current.artistName || current.ownerUsername} · {current.genre}</p></div></div>
        <div className="radio-buttons">
          <button className="btn btn-outline" onClick={previous} aria-label="Previous track">⏮ Previous</button>
          <button className="btn btn-gold radio-play" onClick={toggle}>{playing ? '⏸ Pause' : '▶ Play'}</button>
          <button className="btn btn-outline" onClick={next} aria-label="Next track">Next ⏭</button>
          <button className={`btn ${shuffle ? 'btn-primary' : 'btn-outline'}`} onClick={() => setShuffle(!shuffle)} aria-pressed={shuffle}>Shuffle</button>
        </div>
        <p className="muted">Track {index + 1} of {playable.length} · The station continues automatically.</p>
      </> : <p className="empty">No playable tracks are on The Wave yet. Approved tracks will automatically join this station.</p>}
      {error && <p role="alert">{error}</p>}
    </div>
    <div className="card"><h2>Station catalog</h2><div className="radio-catalog">{playable.map((song, i) => <button key={song.id} className={`radio-catalog-track${i === index ? ' active' : ''}`} onClick={() => { setIndex(i); setPlaying(true); }}><span>{i + 1}</span><strong>{song.title}</strong><small>{song.artistName || song.ownerUsername} · {song.genre}</small></button>)}</div></div>
  </div>;
}
